import { CategoriesApi, CompanyCatalogApi, GroupsApi } from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFilters } from "~/composables/filters/useFilters";
import { useFeedback } from "~/composables/feedback/useFeedback";
import type { IHomeCatalogStore } from "~/utils/interfaces/catalog-interfaces";
import { usePagination } from "~/composables/paginations/usePagination";
import { useAuthStore } from "~/store/auth/auth.store";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { TrainingStatus } from "~/utils/enums/training.enums";

export const useHomeCatalogStore = defineStore("homeCatalogStore", {
  state: (): IHomeCatalogStore => {
    return {
      companyCatalog: null,
      offset: 0,
      maxOffset: 0,
      catalogFetchLoading: false,
      categoryCatalog: null,
      selectedCategory: null,
      searchCatalog: null,
      showTrainingPageModal: false,
      hideCatalogPrices: false,
    };
  },
  getters: {},
  actions: {
    resetPaginate() {
      const paginate = usePagination();
      this.companyCatalog = null;
      this.offset = 0;
      this.maxOffset = 0;
      paginate.reset();
    },
    async fetchCompanyCatalog(fetchByScroll?: boolean) {
      this.catalogFetchLoading = true;
      const paginate = usePagination();
      const filters = useFilters();
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompanyCatalogApi);

      if (!fetchByScroll) {
        paginate.resetOffset();
      }

      const paginateOptions = paginate.getFindOptions(paginate.paginateOptions.value);
      const paginateQuery = new URLSearchParams(paginateOptions);
      const queryString = Object.fromEntries(paginateQuery.entries());
      const { filters: sdkFilters } = filters.getFiltersForSDK();

      // Scope visibility to the user's groups when available (mirrors the legacy only_visible param)
      const auth = useAuthStore();
      let onlyVisible = "only_visible=visible";
      if (auth.user?.userId) {
        try {
          const groupsApi = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);
          const groups = await groupsApi.getUserGroups({ userId: auth.user.userId });
          const groupIds = groups?.attachedGroups?.map((group) => group.groupId) ?? [];
          if (groupIds.length) {
            onlyVisible = `only_visible=visible,${groupIds.join(",")}`;
          }
        } catch {
          // best-effort group scoping; fall back to visible-only
        }
      }

      try {
        const data = await apiInstance.getCompanyCatalog({
          ...queryString,
          sortingColumn: paginateOptions.sortingColumn,
          categoryId: this.selectedCategory?.categoryId,
          filters: [...sdkFilters, `training_status=${TrainingStatus.LIVE}`, "my_catalog=true", onlyVisible],
        });

        if (this.companyCatalog === null) this.companyCatalog = [];
        if (fetchByScroll) {
          this.companyCatalog.push(...data.data);
        } else {
          this.companyCatalog = data.data;
        }
        this.offset += data.data.length;
        this.maxOffset = data.meta.count;
        this.hideCatalogPrices = data.meta.hideCatalogPrices ?? false;
        paginate.paginateOptions.value.offset = this.offset;
      } catch (error) {
        feedback.error(`erreur`, "small");
      } finally {
        this.catalogFetchLoading = false;
      }
    },
    async fetchCatalogCategories() {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CategoriesApi);
        this.categoryCatalog = await apiInstance.findCatalogCategories({});
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },
  },
});
